import React, { useCallback, useEffect, useMemo } from 'react';
import { snakeCase } from 'lodash';
import { ResponsiveBar } from '@nivo/bar';
import { categoricalColorSchemes } from '@nivo/colors';
import { BasicTooltip } from '@nivo/tooltip';
import { BarChartQueryBuilderPlugin, useRunQuery } from '@sierralabs/query-ui';
import { parseTaskStatus } from '@sierralabs/workflow-core';
import SiteConfigChart from '../../config/SiteConfigChart';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { SiteComponent } from '../SiteComponentPlugin';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import ChartLegends from '../../common/charts/ChartLegends';
import { CoreEvent } from '@sierralabs/core-ui';
import { ChartItemsEvent, ChartType } from './ChartItemsDialogPlugin';
import { SiteComponentStackConfigurator } from './SiteComponentStackPlugin';
import ComponentRenderer from '../renderers/ComponentRenderer';
const getFieldName = (field) => {
    // format field name to match with raw data
    if (!field)
        return '';
    const [alias, property] = field.split('.');
    return alias.concat('_', snakeCase(property));
};
const formatDate = (date) => {
    const newDate = new Date(date);
    const year = newDate.getFullYear();
    const month = ('0' + (newDate.getMonth() + 1)).slice(-2);
    return `${year}-${month}`;
};
export const SiteBarChart = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const component = useSubscribeComponentChanges(SiteBarChartPlugin, props);
    const { isHover, hoverProps } = useSiteContext();
    const { queryData, runQuery } = useRunQuery();
    const { nivo } = categoricalColorSchemes;
    const query = (_a = component.props) === null || _a === void 0 ? void 0 : _a.query;
    const barChartParams = (_f = (_e = (_d = (_c = (_b = component.props) === null || _b === void 0 ? void 0 : _b.query) === null || _c === void 0 ? void 0 : _c.config) === null || _d === void 0 ? void 0 : _d.pluginConfigs) === null || _e === void 0 ? void 0 : _e.find(plugin => plugin.type === BarChartQueryBuilderPlugin.type)) === null || _f === void 0 ? void 0 : _f.params;
    const { enableCombiningValues, group } = barChartParams || {};
    const fieldName = getFieldName(barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.field);
    const groupColumnName = getFieldName(group === null || group === void 0 ? void 0 : group.columnName);
    const withChartDialog = (_g = component.children) === null || _g === void 0 ? void 0 : _g.find(child => child.type === 'chart-items-dialog');
    const classes = ['site-pie-chart'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const getLabel = useCallback((value) => {
        return (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.forWorkflowStatus) && typeof value === 'string'
            ? parseTaskStatus(value)
            : value;
    }, [barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.forWorkflowStatus]);
    const formatLabel = (label) => {
        if ((barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.integerTicksOnly) &&
            typeof label === 'number' &&
            Math.floor(label) !== label) {
            return '';
        }
        else {
            const formattedLabel = getLabel(label) || '';
            return formattedLabel.length > 15 ? (React.createElement("tspan", null,
                formattedLabel.substring(0, 15) + '...',
                React.createElement("title", null, formattedLabel))) : (formattedLabel);
        }
    };
    const getAxisProps = (options) => {
        return {
            format: formatLabel,
            tickSize: 5,
            tickPadding: 5,
            legendPosition: 'middle',
            tickRotation: 0,
            legend: options === null || options === void 0 ? void 0 : options.legend,
            legendOffset: options === null || options === void 0 ? void 0 : options.offset
        };
    };
    const handleGrouping = useCallback((data, queryData) => {
        // format the value of selected group column name on all data
        // if having the specified column type
        const formattedData = data.map((item, index) => {
            const value = queryData[index][groupColumnName];
            const updatedValue = (group === null || group === void 0 ? void 0 : group.columnType) === 'timestamp' || (group === null || group === void 0 ? void 0 : group.columnType) === 'date'
                ? formatDate(value)
                : value;
            return (item = Object.assign(Object.assign({}, item), { [groupColumnName]: updatedValue }));
        });
        return formattedData.reduce((results, item) => {
            const existingRecord = results.find((result) => result[groupColumnName] === item[groupColumnName]);
            if (existingRecord) {
                existingRecord[item[fieldName]] = item.value;
            }
            else {
                results.push({
                    [groupColumnName]: item[groupColumnName],
                    [item[fieldName]]: item.value
                });
            }
            return results;
        }, []);
    }, [fieldName, groupColumnName, group === null || group === void 0 ? void 0 : group.columnType]);
    const barChartStats = useMemo(() => {
        if (!barChartParams || !queryData)
            return [];
        const allStats = (queryData || []).reduce((results, item) => {
            const existingItem = results.find((result) => {
                // use the parsed label if enableCombiningValues enabled
                // to properly match the item (esp. use case for workflow status)
                const resultLabel = enableCombiningValues
                    ? getLabel(result[fieldName])
                    : result[fieldName];
                return resultLabel === getLabel(item[fieldName]);
            });
            // handle combining item count for duplicate label
            if (enableCombiningValues && existingItem) {
                existingItem.value = existingItem.value + item.total;
            }
            else {
                results.push({
                    [fieldName]: [null, 'null'].includes(item[fieldName])
                        ? 'null'
                        : item[fieldName],
                    value: item.total
                });
            }
            return results;
        }, []);
        // handle grouping of data based on selected group column when enabled
        return group && group.columnName
            ? handleGrouping(allStats, queryData)
            : allStats;
    }, [
        barChartParams,
        enableCombiningValues,
        fieldName,
        group,
        queryData,
        handleGrouping,
        getLabel
    ]);
    const legendsData = useMemo(() => {
        let legends = [];
        for (const stat of barChartStats) {
            // get legends for grouped items
            if (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.group) {
                if (!(barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.group.columnName))
                    continue;
                const keys = Object.keys(stat);
                for (const key of keys) {
                    if (!legends.find(legend => legend.label === key) &&
                        key !== groupColumnName) {
                        const parsedLabel = getLabel(key);
                        legends.push({
                            id: enableCombiningValues ? parsedLabel : key,
                            label: parsedLabel,
                            value: stat[key],
                            fieldValue: stat[fieldName]
                        });
                    }
                }
            }
            else {
                const parsedLabel = getLabel(stat[fieldName]);
                legends.push(Object.assign(Object.assign({}, stat), { 
                    // use parsed label as id when combining values is enabled to display
                    // approporate tooltip on legend since there will be no duplicate labels
                    id: enableCombiningValues ? parsedLabel : stat[fieldName], label: parsedLabel, fieldValue: stat[fieldName] }));
            }
        }
        // add color value here to avoid having same color
        return legends.map((legend, index) => {
            const colorIndex = nivo.length === 0
                ? -1
                : index - Math.floor(index / nivo.length) * nivo.length;
            return Object.assign(Object.assign({}, legend), { color: nivo[colorIndex] });
        });
    }, [
        barChartStats,
        enableCombiningValues,
        groupColumnName,
        nivo,
        barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.group,
        fieldName,
        getLabel
    ]);
    const handleItemClick = (value) => {
        CoreEvent.dispatch(ChartItemsEvent.OpenDialog, {
            id: component.id,
            conditions: [{ field: (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.field) || '', value }]
        });
    };
    useEffect(() => {
        if (barChartParams && barChartParams.field) {
            (query === null || query === void 0 ? void 0 : query.config) && runQuery(query.config, true);
        }
    }, [query, query === null || query === void 0 ? void 0 : query.config, barChartParams, runQuery]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteBarChartPlugin }, props),
        React.createElement("div", Object.assign({ style: { padding: 16 } }, hoverProps(component)),
            React.createElement("div", { style: { height: (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.chartHeight) || 300 } },
                React.createElement(ResponsiveBar, { data: barChartStats, onClick: data => {
                        handleItemClick(data.indexValue);
                    }, keys: group
                        ? legendsData.map(legend => legend.id.toString())
                        : ['value'], indexBy: group ? groupColumnName : fieldName, colors: bar => {
                        var _a;
                        const targetLabel = group
                            ? bar.id
                            : enableCombiningValues
                                ? getLabel(bar.indexValue)
                                : bar.indexValue;
                        return (((_a = legendsData.find(legend => legend.id === targetLabel)) === null || _a === void 0 ? void 0 : _a.color) || '');
                    }, layout: barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.layout, tooltip: props => {
                        // use parsed label as tooltip when combining values
                        // is enabled since there will be no duplicate labels
                        const label = enableCombiningValues
                            ? getLabel(props.indexValue)
                            : props.indexValue;
                        return (React.createElement(BasicTooltip, { id: `${props.id} - ${label}`, value: props.value, color: props.color, enableChip: true }));
                    }, margin: {
                        top: (_j = (_h = barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisTop) === null || _h === void 0 ? void 0 : _h.margin) !== null && _j !== void 0 ? _j : 40,
                        right: (_l = (_k = barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisRight) === null || _k === void 0 ? void 0 : _k.margin) !== null && _l !== void 0 ? _l : 40,
                        bottom: (_o = (_m = barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisBottom) === null || _m === void 0 ? void 0 : _m.margin) !== null && _o !== void 0 ? _o : 40,
                        left: (_q = (_p = barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisLeft) === null || _p === void 0 ? void 0 : _p.margin) !== null && _q !== void 0 ? _q : ((barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.layout) === 'horizontal' ? 100 : 50)
                    }, borderRadius: 5, padding: 0.3, groupMode: 'grouped', valueScale: { type: 'linear' }, indexScale: { type: 'band', round: true }, borderColor: { from: 'color', modifiers: [['darker', 1.6]] }, axisTop: (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisTop)
                        ? getAxisProps(barChartParams.axisTop)
                        : null, axisRight: (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisRight)
                        ? getAxisProps(barChartParams.axisRight)
                        : null, axisBottom: getAxisProps(barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisBottom), axisLeft: getAxisProps(barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.axisLeft), labelSkipWidth: 12, labelSkipHeight: 12, labelTextColor: { from: 'color', modifiers: [['darker', 1.6]] }, enableGridX: true, animate: true, onMouseEnter: (_data, event) => {
                        if (!!withChartDialog) {
                            event.target.style.cursor = 'pointer';
                        }
                    } })),
            (barChartParams === null || barChartParams === void 0 ? void 0 : barChartParams.showLegends) && (React.createElement(ChartLegends, { stats: legendsData, onItemClick: stat => {
                    handleItemClick(stat.fieldValue);
                } }))), (_r = component.children) === null || _r === void 0 ? void 0 :
        _r.map(child => (React.createElement(ComponentRenderer, { key: child.id, component: child })))));
};
export const SiteBarChartConfigurator = props => {
    const { component } = useConfigComponent(props.component);
    return (React.createElement(React.Fragment, null,
        React.createElement(SiteConfigChart, { component: component, plugin: BarChartQueryBuilderPlugin }),
        React.createElement(SiteComponentStackConfigurator, { component: props.component })));
};
export const SiteBarChartPlugin = {
    name: 'Bar Chart',
    type: ChartType.Bar,
    component: SiteBarChart,
    configurator: SiteBarChartConfigurator,
    initConfig: component => {
        if (!component.props)
            component.props = {};
        // add BarChart Data set on component query config during initialization
        component.props.query = {
            config: {
                pluginConfigs: [{ type: BarChartQueryBuilderPlugin.type, params: {} }]
            }
        };
        return component;
    },
    slots: [
        {
            name: 'Chart Items Dialog',
            type: 'chart-items-dialog',
            slot: 'dialog'
        }
    ]
};
//# sourceMappingURL=SiteBarChartPlugin.js.map